import React, { Component } from "react";
import PropertyImageScrapeService from "../../../services/PropertyImageScrapeService";
import PropertyService from "../../../services/PropertyService";

/**
 * PropertyImageScraper
 *
 * Lets an admin confirm scrape permission for a property, submit a listing URL,
 * monitor pending scraped images as they arrive, and approve or reject each one.
 *
 * Route: /properties/view/:propertySlug/scrape-images
 * Props: propertySlug (string)
 */
class PropertyImageScraper extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Permission section
            permissionConfirmed: false,
            permissionNotes: "",
            savingPermission: false,

            // Scrape trigger section
            scrapeUrl: "",
            isQueuing: false,
            queueMessage: "",
            queueError: "",

            // Image gallery
            images: [],
            loadingImages: false,
            galleryFilter: "pending",
            pollingActive: false,

            // Toast feedback
            toastMessage: "",
            toastType: "success",
        };

        // Polling interval reference — cleared in componentWillUnmount
        this.pollingTimer = null;
    }

    componentDidMount() {
        this.loadProperty();
        this.loadImages();
    }

    componentWillUnmount() {
        // Always clear the interval so it does not run after the component is gone
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
    }

    /**
     * Load current scrape_permission_confirmed value from the existing property record.
     */
    loadProperty() {
        const { propertySlug } = this.props;
        if (!propertySlug) return;

        PropertyService.getProperty(propertySlug).then((res) => {
            if (global.successStatus.includes(res.status)) {
                const prop = res.data || {};
                this.setState({
                    permissionConfirmed: !!prop.scrape_permission_confirmed,
                    permissionNotes: prop.scrape_permission_notes || "",
                });
            }
        });
    }

    /**
     * Fetch scraped images from the API, optionally filtered by the current gallery tab.
     */
    loadImages(filter) {
        const { propertySlug } = this.props;
        const reviewStatus = filter !== undefined ? filter : this.state.galleryFilter;

        this.setState({ loadingImages: true });

        PropertyImageScrapeService.getScrapedImages(propertySlug, reviewStatus).then((res) => {
            if (global.successStatus.includes(res.status)) {
                this.setState({ images: res.data || [], loadingImages: false });
            } else {
                this.setState({ loadingImages: false });
            }
        });
    }

    /**
     * Save the permission flag and notes back to the property record.
     */
    handleSavePermission() {
        const { propertySlug } = this.props;
        const { permissionConfirmed, permissionNotes } = this.state;

        // Permission gate — user must have the right to edit property settings
        if (!global.userPermissions.checkPermission("property-edit")) {
            this.showToast("You do not have permission to update property settings.", "danger");
            return;
        }

        this.setState({ savingPermission: true });

        PropertyImageScrapeService.updateScrapePermission(
            propertySlug,
            permissionConfirmed,
            permissionNotes
        ).then((res) => {
            this.setState({ savingPermission: false });
            if (global.successStatus.includes(res.status)) {
                this.showToast("Permission settings saved.", "success");
            } else {
                this.showToast(res.message || "Could not save permission.", "danger");
            }
        });
    }

    /**
     * Submit the scrape URL to the backend — the job is dispatched asynchronously.
     */
    handleQueueScrape(e) {
        e.preventDefault();
        const { propertySlug } = this.props;
        const { scrapeUrl, permissionConfirmed } = this.state;

        if (!global.userPermissions.checkPermission("property-edit")) {
            this.showToast("You do not have permission to start a scrape.", "danger");
            return;
        }

        if (!permissionConfirmed) {
            this.setState({ queueError: "Enable scrape permission before starting a scrape." });
            return;
        }

        if (!scrapeUrl.trim()) {
            this.setState({ queueError: "Please enter a listing URL." });
            return;
        }

        this.setState({ isQueuing: true, queueError: "", queueMessage: "" });

        PropertyImageScrapeService.queueScrape(propertySlug, scrapeUrl.trim()).then((res) => {
            this.setState({ isQueuing: false });
            if (global.successStatus.includes(res.status)) {
                this.setState({
                    queueMessage: "Scrape job queued. Images will appear in the gallery below as they are processed.",
                    scrapeUrl: "",
                });
                // Start polling every 10 s so new images show up automatically
                this.startPolling();
            } else {
                this.setState({ queueError: res.message || "Could not queue the scrape job." });
            }
        });
    }

    /**
     * Start a 10-second polling loop to refresh the pending-images gallery.
     * Only one timer runs at a time.
     */
    startPolling() {
        if (this.pollingTimer) return;
        this.setState({ pollingActive: true });
        this.pollingTimer = setInterval(() => {
            this.loadImages("pending");
        }, 10000);
    }

    /**
     * Stop auto-refresh.
     */
    stopPolling() {
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
        this.setState({ pollingActive: false });
    }

    /**
     * Approve or reject a single scraped image and refresh the gallery.
     */
    handleReviewImage(id, decision) {
        if (!global.userPermissions.checkPermission("property-edit")) {
            this.showToast("You do not have permission to review images.", "danger");
            return;
        }

        PropertyImageScrapeService.reviewImage(this.props.propertySlug, id, decision).then((res) => {
            if (global.successStatus.includes(res.status)) {
                this.showToast(
                    decision === "approved" ? "Image approved." : "Image rejected.",
                    decision === "approved" ? "success" : "warning"
                );
                // Reload the current tab after each decision
                this.loadImages();
            } else {
                this.showToast(res.message || "Could not save decision.", "danger");
            }
        });
    }

    /**
     * Switch gallery tab and reload images for the selected filter.
     */
    handleFilterChange(filter) {
        this.setState({ galleryFilter: filter }, () => {
            this.loadImages(filter);
        });
    }

    showToast(message, type) {
        this.setState({ toastMessage: message, toastType: type });
        setTimeout(() => this.setState({ toastMessage: "" }), 4000);
    }

    render() {
        const {
            permissionConfirmed,
            permissionNotes,
            savingPermission,
            scrapeUrl,
            isQueuing,
            queueMessage,
            queueError,
            images,
            loadingImages,
            galleryFilter,
            pollingActive,
            toastMessage,
            toastType,
        } = this.state;

        return (
            <div className="container-fluid px-4 py-3">
                {/* Toast notification */}
                {toastMessage && (
                    <div className={"alert alert-" + toastType + " alert-dismissible"} role="alert">
                        {toastMessage}
                        <button
                            type="button"
                            className="close"
                            onClick={() => this.setState({ toastMessage: "" })}
                        >
                            <span>&times;</span>
                        </button>
                    </div>
                )}

                <h4 className="mb-4">Property Image Scraper</h4>

                {/* ── Permission section ── */}
                <div className="card mb-4">
                    <div className="card-header">
                        <strong>Scrape Permission</strong>
                    </div>
                    <div className="card-body">
                        <p className="text-muted small mb-3">
                            The host must confirm that scraping images from external listing pages
                            is permitted before any job can be dispatched.
                        </p>
                        <div className="form-group d-flex align-items-center mb-3">
                            <div className="custom-control custom-switch mr-3">
                                <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id="scrapePermissionSwitch"
                                    checked={permissionConfirmed}
                                    onChange={(e) =>
                                        this.setState({ permissionConfirmed: e.target.checked })
                                    }
                                />
                                <label
                                    className="custom-control-label"
                                    htmlFor="scrapePermissionSwitch"
                                >
                                    {permissionConfirmed
                                        ? "Permission confirmed"
                                        : "Permission not confirmed"}
                                </label>
                            </div>
                            <span
                                className={
                                    "badge " +
                                    (permissionConfirmed ? "badge-success" : "badge-secondary")
                                }
                            >
                                {permissionConfirmed ? "Enabled" : "Disabled"}
                            </span>
                        </div>
                        <div className="form-group">
                            <label htmlFor="permissionNotes">Notes (optional)</label>
                            <textarea
                                id="permissionNotes"
                                className="form-control"
                                rows="2"
                                placeholder="Why was permission granted or denied?"
                                value={permissionNotes}
                                onChange={(e) =>
                                    this.setState({ permissionNotes: e.target.value })
                                }
                            />
                        </div>
                        <button
                            className="btn btn-primary"
                            onClick={() => this.handleSavePermission()}
                            disabled={savingPermission}
                        >
                            {savingPermission ? "Saving..." : "Save Permission Settings"}
                        </button>
                    </div>
                </div>

                {/* ── Scrape trigger section ── */}
                <div className="card mb-4">
                    <div className="card-header">
                        <strong>Start a Scrape</strong>
                    </div>
                    <div className="card-body">
                        <p className="text-muted small mb-3">
                            Enter the public listing URL. The system will fetch images in the
                            background, classify each room type using AI, and add them to the
                            pending review queue below.
                        </p>
                        <form onSubmit={(e) => this.handleQueueScrape(e)}>
                            <div className="form-group">
                                <label htmlFor="scrapeUrl">Listing URL</label>
                                <div className="input-group">
                                    <input
                                        id="scrapeUrl"
                                        type="url"
                                        className="form-control"
                                        placeholder="https://example.com/listing/..."
                                        value={scrapeUrl}
                                        onChange={(e) =>
                                            this.setState({
                                                scrapeUrl: e.target.value,
                                                queueError: "",
                                            })
                                        }
                                        disabled={isQueuing}
                                    />
                                    <div className="input-group-append">
                                        <button
                                            type="submit"
                                            className="btn btn-success"
                                            disabled={isQueuing || !permissionConfirmed}
                                        >
                                            {isQueuing ? "Queuing..." : "Start Scraping"}
                                        </button>
                                    </div>
                                </div>
                                {!permissionConfirmed && (
                                    <small className="text-warning">
                                        Enable scrape permission above before starting.
                                    </small>
                                )}
                            </div>
                            {queueError && (
                                <div className="alert alert-danger py-2">{queueError}</div>
                            )}
                            {queueMessage && (
                                <div className="alert alert-info py-2">{queueMessage}</div>
                            )}
                        </form>
                    </div>
                </div>

                {/* ── Image gallery ── */}
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <strong>Scraped Images</strong>
                        <div className="d-flex align-items-center">
                            {pollingActive && (
                                <span className="badge badge-info mr-3">
                                    Auto-refreshing every 10 s
                                </span>
                            )}
                            <button
                                className="btn btn-sm btn-outline-secondary mr-2"
                                onClick={() => this.loadImages()}
                                title="Refresh now"
                            >
                                Refresh
                            </button>
                            {pollingActive ? (
                                <button
                                    className="btn btn-sm btn-outline-warning"
                                    onClick={() => this.stopPolling()}
                                >
                                    Stop Auto-refresh
                                </button>
                            ) : (
                                <button
                                    className="btn btn-sm btn-outline-info"
                                    onClick={() => this.startPolling()}
                                >
                                    Start Auto-refresh
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filter tabs */}
                    <div className="card-header py-2">
                        <ul className="nav nav-tabs card-header-tabs">
                            {["pending", "approved", "rejected", ""].map((tab) => (
                                <li className="nav-item" key={tab || "all"}>
                                    <button
                                        className={
                                            "nav-link btn btn-link " +
                                            (galleryFilter === tab ? "active" : "")
                                        }
                                        onClick={() => this.handleFilterChange(tab)}
                                    >
                                        {tab === "" ? "All" : tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="card-body">
                        {loadingImages && (
                            <p className="text-muted text-center py-4">Loading images...</p>
                        )}

                        {!loadingImages && images.length === 0 && (
                            <p className="text-muted text-center py-4">
                                No{galleryFilter ? " " + galleryFilter : ""} scraped images found
                                for this property.
                            </p>
                        )}

                        {!loadingImages && images.length > 0 && (
                            <div className="row">
                                {images.map((img) => (
                                    <div
                                        className="col-sm-6 col-md-4 col-lg-3 mb-4"
                                        key={img.media_file_id}
                                    >
                                        <div className="card h-100">
                                            <img
                                                src={img.source_url}
                                                alt={img.common_area_type || "scraped"}
                                                className="card-img-top"
                                                style={{ height: "160px", objectFit: "cover" }}
                                                onError={(e) => {
                                                    e.target.style.display = "none";
                                                }}
                                            />
                                            <div className="card-body p-2">
                                                <p className="mb-1 small text-capitalize font-weight-bold">
                                                    {img.common_area_type
                                                        ? img.common_area_type.replace(/_/g, " ")
                                                        : "Unknown"}
                                                </p>
                                                <p className="mb-1">
                                                    <span
                                                        className={
                                                            "badge badge-" +
                                                            (img.review_status === "approved"
                                                                ? "success"
                                                                : img.review_status === "rejected"
                                                                ? "danger"
                                                                : "warning")
                                                        }
                                                    >
                                                        {img.review_status}
                                                    </span>
                                                </p>
                                                {img.phash && (
                                                    <p
                                                        className="text-muted mb-0"
                                                        style={{ fontSize: "10px" }}
                                                        title="Perceptual hash"
                                                    >
                                                        {img.phash}
                                                    </p>
                                                )}
                                            </div>
                                            {/* Only show review buttons when image is pending */}
                                            {img.review_status === "pending" && (
                                                <div className="card-footer p-2 d-flex justify-content-between">
                                                    <button
                                                        className="btn btn-sm btn-success flex-fill mr-1"
                                                        onClick={() =>
                                                            this.handleReviewImage(
                                                                img.media_file_id,
                                                                "approved"
                                                            )
                                                        }
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger flex-fill ml-1"
                                                        onClick={() =>
                                                            this.handleReviewImage(
                                                                img.media_file_id,
                                                                "rejected"
                                                            )
                                                        }
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default PropertyImageScraper;
